/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { UnitUtility } from '@bmsp/bmsp.web.component.common';

import {
  IEmaasCarbonData,
  IEmaasEnergyUsageData,
  IEmaasEuiData,
  IMSEmaaSPortfolioSiteTableProps,
  IProgressBarData,
  IProgressBarEui,
  IProgressBarEUIEnergyDetails,
  ISiteCarbonData,
  ISiteEnergyUsageData,
  ISiteEuiData
} from '../../DashboardKPIWidgets/EMaaSMobileSupervisorPortfolioDashboard/IPortfolioProps';
import { IAny } from '../../DashboardKPIWidgets/IEnergyManagementProps';
import { EnergyUsageField } from '../Enums';
import {
  KPI_PERCENTAGE_DECIMAL_PRECISION,
  KPI_VALUE_DECIMAL_PRECISION,
  PERCENT_CHANGE_THRESHOLD,
  PERCENTAGE_HUNDRED,
  ZERO_DEFAULT_VALUE
} from './Constants';
import { getNullToZero } from './EnergyUsagesViewDetailsUtils';
import { consumFields, energyFields } from './types';
import { handleEuiUnitConversion } from './unitHandling/EuiUnitHandling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray,
  IMSEmaaSPortfolioSiteTablePropsCheck,
  IMSEmaaSPDeviationPercentageCheck,
  IMSEmaaSMessageCheck
} from './unitHandling/unitHandling';
import { ConversionType } from './unitHandling/UnitModel';
import { EuiEnergyConsumptionCheck } from './Utils';

export const getSiteListForEnergy = (
  emaasEnergyDataList: Array<IEmaasEnergyUsageData>
) => {
  if (emaasEnergyDataList && emaasEnergyDataList.length > 0) {
    return emaasEnergyDataList.map((data: IEmaasEnergyUsageData) => {
      return data && data.site;
    });
  }
};

export const getSiteListForCarbon = (
  emaasCarbonDataList: Array<IEmaasCarbonData>
) => {
  if (emaasCarbonDataList && emaasCarbonDataList.length > 0) {
    return emaasCarbonDataList.map((data: IEmaasCarbonData) => {
      return data && data.site;
    });
  }
};

export const getSiteListForEui = (emaasEuiDataList: Array<IEmaasEuiData>) => {
  if (emaasEuiDataList && emaasEuiDataList.length > 0) {
    return emaasEuiDataList.map((data: IEmaasEuiData) => {
      return data && data.site;
    });
  }
};

export const convertDataForEnergyUsage = (
  breakdownBySite: Array<ISiteEnergyUsageData>,
  unit: string,
  visibleSites: string[],
  isPremiumSubscriptions: boolean,
  originalUnit: string,
  showChangeWhenData: boolean = false
) => {
  const valuesArray = [
    getConsupSumFromSite(breakdownBySite, 'totalConsumption'),
    getConsupSumFromSite(breakdownBySite, 'totalBaselineConsumption')
  ];
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.ELECTRICITY,
    unit,
    valuesArray,
    originalUnit
  );

  const tableData: Array<IMSEmaaSPortfolioSiteTableProps> = [];
  breakdownBySite.forEach((data) => {
    if (data) {
      const {
        name,
        id,
        totalConsumption,
        totalEnergyDeviation,
        totalEnergyDeviationPercentage,
        totalBaselineConsumption
      } = data;
      const percentChange = parseFloat(
        totalEnergyDeviationPercentage?.toFixed(
          KPI_PERCENTAGE_DECIMAL_PRECISION
        )
      );

      if (visibleSites.includes(id)) {
        let meterSavings: IMSEmaaSPortfolioSiteTableProps = {
          SiteName: name,
          Value: totalConsumption,
          Deviation:
            showChangeWhenData &&
            (isNaN(Number(totalBaselineConsumption)) ||
              isNaN(Number(totalConsumption)) ||
              Number(totalConsumption) === 0 ||
              Number(totalBaselineConsumption) === 0 ||
              Math.abs(Number(percentChange)) > PERCENT_CHANGE_THRESHOLD)
              ? null
              : totalEnergyDeviation,
          DeviationPercentage: parseFloat(
            totalEnergyDeviationPercentage?.toFixed(
              KPI_PERCENTAGE_DECIMAL_PRECISION
            )
          ),
          Unit: getMaxConvertedValueFromArray(
            ConversionType.ELECTRICITY,
            unit,
            [totalConsumption],
            originalUnit
          ).Unit,
          deviationUnit: getMaxConvertedValueFromArray(
            ConversionType.ELECTRICITY,
            unit,
            [totalEnergyDeviation],
            originalUnit
          ).Unit,
          SiteId: id
        };
        tableData.push(meterSavings);
      }
    }
  });
  //Calculating data for progress bar
  const { actualField, baselineField } = getEnergyFieldsForDeviation(
    isPremiumSubscriptions
  );
  let actualPreviousData = breakdownBySite.reduce(
    (prev, item) => {
      return {
        actual:
          prev.actual + getFieldValue(visibleSites, item, 'totalConsumption')!,
        previousPeriod:
          prev.previousPeriod +
          getFieldValue(visibleSites, item, 'totalBaselineConsumption')!,
        absoluteActual:
          prev.absoluteActual + getFieldValue(visibleSites, item, actualField)!,
        absolutePrevPeriod:
          prev.absolutePrevPeriod +
          getFieldValue(visibleSites, item, baselineField)!
      };
    },
    {
      actual: ZERO_DEFAULT_VALUE,
      previousPeriod: ZERO_DEFAULT_VALUE,
      absoluteActual: ZERO_DEFAULT_VALUE,
      absolutePrevPeriod: ZERO_DEFAULT_VALUE
    }
  );

  let deviation =
    showChangeWhenData &&
    (isNaN(Number(actualPreviousData.previousPeriod)) ||
      isNaN(Number(actualPreviousData.actual)) ||
      Number(actualPreviousData?.actual) === 0 ||
      Number(actualPreviousData?.previousPeriod) === 0)
      ? null
      : getNullToZero(actualPreviousData?.previousPeriod) -
        getNullToZero(actualPreviousData?.actual);
  if (
    deviation !== null &&
    showChangeWhenData &&
    Math.abs(
      Number(
        (deviation / actualPreviousData.previousPeriod) * PERCENTAGE_HUNDRED
      )
    ) > PERCENT_CHANGE_THRESHOLD
  ) {
    deviation = null;
  }
  let progressBarData: IProgressBarData = {
    ...actualPreviousData,
    deviation: deviation,
    deviationPercentage:
      deviation && actualPreviousData?.absolutePrevPeriod
        ? parseFloat(
            (
              (deviation / actualPreviousData?.absolutePrevPeriod) *
              PERCENTAGE_HUNDRED
            )?.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
          )
        : ZERO_DEFAULT_VALUE
  };

  progressBarData = {
    deviationPercentage: progressBarData.deviationPercentage,
    ...getProgressBarUnitScaledData(
      ConversionType.ELECTRICITY,
      {
        previousPeriod: progressBarData.previousPeriod,
        actual: progressBarData.actual
      },
      progressBarData.deviation,
      unit, //maxValueUnitConversion.Unit, //
      undefined,
      actualPreviousData.absolutePrevPeriod,
      actualPreviousData.absoluteActual,
      originalUnit,
      showChangeWhenData,
      true
    )
  };
  let deviationScaledData;
  if (progressBarData.deviation === 0) {
    const totaldeviationBeforeScaling = getDeviationBeforeScaling(
      breakdownBySite,
      actualField,
      baselineField
    );
    if (totaldeviationBeforeScaling !== 0) {
      deviationScaledData = getConvertedValue(
        ConversionType.ELECTRICITY,
        originalUnit,
        totaldeviationBeforeScaling,
        maxValueUnitConversion.Unit
      );
    }
  }
  return {
    tableData,
    progressBarData,
    displayUnit: maxValueUnitConversion.Unit,
    deviationScaledData
  };
};
const getDeviationBeforeScaling = (
  breakdownBySite: ISiteEnergyUsageData[],
  actualField: energyFields,
  baselineField: energyFields
) => {
  const totalDeviation = breakdownBySite.reduce((prev, curr) => {
    return prev + curr[baselineField]! - curr[actualField]!;
  }, 0);
  return totalDeviation;
};
export const convertDataForCarbon = (
  breakdownBySite: Array<ISiteCarbonData>,
  unit: string,
  visibleSites: string[],
  showChangeWhenData: boolean = false
) => {
  const valuesArray = [
    getConsupSumFromSite(breakdownBySite, 'totalActualEmission'),
    getConsupSumFromSite(breakdownBySite, 'totalPreviousPeriodEmission')
  ];
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    unit,
    valuesArray
  );

  const tableData: Array<IMSEmaaSPortfolioSiteTableProps> = [];
  breakdownBySite.forEach((data) => {
    if (data) {
      const {
        siteName,
        siteId,
        totalActualEmission,
        totalDeviation,
        totalDeviationPercentage,
        totalPreviousPeriodEmission
      } = data;
      if (visibleSites.includes(siteId)) {
        const percentChange = parseFloat(
          totalDeviationPercentage?.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
        );
        let siteData: IMSEmaaSPortfolioSiteTableProps = {
          SiteName: siteName,
          Value: totalActualEmission,
          Deviation:
            showChangeWhenData &&
            (isNaN(Number(totalPreviousPeriodEmission)) ||
              isNaN(Number(totalActualEmission)) ||
              Number(totalPreviousPeriodEmission) === 0 ||
              Number(totalActualEmission) === 0 ||
              (percentChange && Math.abs(Number(percentChange))) >
                PERCENT_CHANGE_THRESHOLD)
              ? null
              : totalDeviation,
          DeviationPercentage: parseFloat(
            totalDeviationPercentage?.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
          ),
          Unit: getMaxConvertedValueFromArray(ConversionType.CARBON, unit, [
            totalActualEmission
          ]).Unit,
          deviationUnit: getMaxConvertedValueFromArray(
            ConversionType.CARBON,
            unit,
            [totalDeviation]
          ).Unit,
          SiteId: siteId
        };
        tableData.push(siteData);
      }
    }
  });
  let actualPreviousData = breakdownBySite.reduce(
    (prev, item) => {
      return {
        actual:
          prev.actual +
          (visibleSites.includes(item && item.siteId)
            ? item.totalActualEmission
            : ZERO_DEFAULT_VALUE),
        previousPeriod:
          prev.previousPeriod +
          (visibleSites.includes(item && item.siteId)
            ? item.totalPreviousPeriodEmission
            : ZERO_DEFAULT_VALUE)
      };
    },
    {
      actual: ZERO_DEFAULT_VALUE,
      previousPeriod: ZERO_DEFAULT_VALUE
    }
  );

  let deviation =
    showChangeWhenData &&
    (isNaN(Number(actualPreviousData.previousPeriod)) ||
      isNaN(Number(actualPreviousData.actual)) ||
      Number(actualPreviousData?.actual) === 0 ||
      Number(actualPreviousData?.previousPeriod) === 0)
      ? null
      : getNullToZero(actualPreviousData?.previousPeriod) -
        getNullToZero(actualPreviousData?.actual);
  if (
    deviation !== null &&
    showChangeWhenData &&
    Math.abs(
      Number(
        (Number(deviation) / Number(actualPreviousData.previousPeriod)) *
          PERCENTAGE_HUNDRED
      )
    ) > PERCENT_CHANGE_THRESHOLD
  ) {
    deviation = null;
  }
  let progressBarData: IProgressBarData = {
    ...actualPreviousData,
    deviation: deviation,
    deviationPercentage:
      deviation && actualPreviousData.previousPeriod
        ? parseFloat(
            (
              (deviation / actualPreviousData.previousPeriod) *
              PERCENTAGE_HUNDRED
            ).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
          )
        : ZERO_DEFAULT_VALUE
  };

  progressBarData = {
    deviationPercentage: progressBarData.deviationPercentage,
    ...getProgressBarUnitScaledData(
      ConversionType.CARBON,
      {
        previousPeriod: progressBarData.previousPeriod,
        actual: progressBarData.actual
      },
      progressBarData.deviation,
      unit,
      undefined,
      null,
      null,
      unit,
      showChangeWhenData,
      true
    )
  };
  return {
    tableData,
    progressBarData,
    displayUnit: maxValueUnitConversion.Unit
  };
};

export const convertDataForEui = (
  breakdownBySite: Array<ISiteEuiData>,
  unit: string,
  preferredEuiUnit: string,
  visibleSites: string[]
) => {
  const tableData: Array<IMSEmaaSPortfolioSiteTableProps> = [];

  breakdownBySite.forEach((data) => {
    if (data) {
      const {
        siteName,
        siteId,
        actualEui,
        euiDeviation,
        euiDeviationPercentage,
        message
      } = data;

      if (visibleSites.includes(siteId)) {
        let siteData: IMSEmaaSPortfolioSiteTableProps = {
          SiteName: siteName,
          Value: IMSEmaaSPortfolioSiteTablePropsCheck(
            message,
            actualEui,
            unit,
            preferredEuiUnit
          ),
          Deviation: IMSEmaaSPortfolioSiteTablePropsCheck(
            message,
            euiDeviation,
            unit,
            preferredEuiUnit
          ),
          DeviationPercentage: IMSEmaaSPDeviationPercentageCheck(
            message,
            euiDeviationPercentage
          ),
          Unit: preferredEuiUnit,
          SiteId: siteId,
          Message: IMSEmaaSMessageCheck(message)
        };
        tableData.push(siteData);
      }
    }
  });

  const progressBarEuiEnergyData: IProgressBarEUIEnergyDetails = breakdownBySite.reduce(
    (prev, item) => {
      const isVisibleSiteId = visibleSites.includes(item && item.siteId);
      const actualEuiEnergyConsumption = EuiEnergyConsumptionCheck(
        item,
        item.actualEuiEnergyConsumption
      );
      const baselineEuiEnergyConsumption = EuiEnergyConsumptionCheck(
        item,
        item.baselineEuiEnergyConsumption
      );
      const areaPerSQft = EuiEnergyConsumptionCheck(item, item.areaPerSQft);
      return getActualAndBaselineEui(
        isVisibleSiteId,
        prev.actualEuiEnergyConsumption,
        actualEuiEnergyConsumption,
        prev.baselineEuiEnergyConsumption,
        baselineEuiEnergyConsumption,
        prev.areaPerSQft,
        areaPerSQft
      );
    },
    {
      actualEuiEnergyConsumption: 0,
      baselineEuiEnergyConsumption: 0,
      areaPerSQft: 0
    }
  );

  let actualEuiSite = progressBarEuiEnergyData.areaPerSQft
    ? progressBarEuiEnergyData.actualEuiEnergyConsumption /
      progressBarEuiEnergyData.areaPerSQft
    : 0;
  let baselineEui = progressBarEuiEnergyData.areaPerSQft
    ? progressBarEuiEnergyData.baselineEuiEnergyConsumption /
      progressBarEuiEnergyData.areaPerSQft
    : 0;

  actualEuiSite = handleEuiUnitConversion(
    actualEuiSite,
    unit,
    preferredEuiUnit
  );
  baselineEui = handleEuiUnitConversion(baselineEui, unit, preferredEuiUnit);

  let progressBarData: IProgressBarEui = {
    actualEui: actualEuiSite || null,
    baselineEui: baselineEui || null,
    euiDeviation:
      actualEuiSite || baselineEui
        ? parseFloat(
            (baselineEui - actualEuiSite).toFixed(KPI_VALUE_DECIMAL_PRECISION)
          )
        : null
  };
  if (progressBarData.euiDeviation && progressBarData.baselineEui) {
    progressBarData.euiDeviationPercentage = parseFloat(
      (
        (progressBarData.euiDeviation / progressBarData.baselineEui) *
        PERCENTAGE_HUNDRED
      ).toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
    );
  } else {
    progressBarData.euiDeviationPercentage = 0;
  }

  return { tableData, progressBarData };
};

const getActualAndBaselineEui = (
  isVisibleSiteId: boolean,
  prevActualEUIEnergy: number,
  currActualEUIEnergy: number,
  prevBaselineEUIEnergy: number,
  currBaselineEUIEnergy: number,
  prevAreaPerSQft: number,
  currAreaPerSQft: number
) => {
  const returnActualEui = () => {
    if (isVisibleSiteId) {
      return prevActualEUIEnergy + currActualEUIEnergy;
    } else {
      return prevActualEUIEnergy;
    }
  };

  const returnBaselineEui = () => {
    if (isVisibleSiteId) {
      return prevBaselineEUIEnergy + currBaselineEUIEnergy;
    } else {
      return prevBaselineEUIEnergy;
    }
  };

  const returnAreaPerSqft = () => {
    if (isVisibleSiteId) {
      return prevAreaPerSQft + currAreaPerSQft;
    } else {
      return prevAreaPerSQft;
    }
  };

  return {
    actualEuiEnergyConsumption: returnActualEui(),
    baselineEuiEnergyConsumption: returnBaselineEui(),
    areaPerSQft: returnAreaPerSqft()
  };
};

export const getProgressBarUnitScaledData = (
  conversionType: string,
  consumption: { previousPeriod: number | null; actual: number | null },
  deviation: number | null,
  fromUnit: string,
  toUnit?: string,
  absolutePreviousPeriod: number | null = consumption.previousPeriod,
  absoluteActual: number | null = consumption.actual,
  originalUnit?: string,
  showChangeWhenData: boolean = false,
  fromCarbonKPI?: boolean
) => {
  let { previousPeriod, actual } = consumption;
  let actualUnit, baselineUnit, changeUnit;
  const originalDeviation: IAny = deviation;
  let ConvertedUnit;
  //This will be used when toUnit is provided, i.e when for table data conversion is done and unit is known
  if (toUnit) {
    previousPeriod = getConvertedValue(
      conversionType,
      fromUnit,
      previousPeriod,
      toUnit
    ).Value;
    absolutePreviousPeriod = getConvertedValue(
      conversionType,
      fromUnit,
      absolutePreviousPeriod,
      toUnit
    ).Value;
    actual = getConvertedValue(conversionType, fromUnit, actual, toUnit).Value;
    absoluteActual = getConvertedValue(
      conversionType,
      fromUnit,
      absoluteActual,
      toUnit
    ).Value;
    deviation =
      showChangeWhenData &&
      (isNaN(Number(previousPeriod)) ||
        isNaN(Number(actual)) ||
        Number(actual) === 0 ||
        Number(previousPeriod) === 0)
        ? null
        : getConvertedValue(conversionType, fromUnit, deviation, toUnit).Value;
    ConvertedUnit = toUnit;
  } else {
    const convertedPreviousPeriod = !previousPeriod ? 0 : previousPeriod;
    const convertedActual = !actual ? 0 : actual;
    const maxValue: IAny =
      Math.abs(convertedPreviousPeriod) > Math.abs(convertedActual)
        ? previousPeriod
        : actual;
    const MaxValueUnitConversion = getMaxConvertedValueFromArray(
      conversionType,
      fromUnit,
      [maxValue],
      originalUnit
    );
    if (maxValue == previousPeriod) {
      previousPeriod = fromCarbonKPI
        ? convertedPreviousPeriod
        : MaxValueUnitConversion.Value;
      baselineUnit = getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [convertedPreviousPeriod],
        originalUnit
      ).Unit;
      absolutePreviousPeriod = getConvertedValue(
        conversionType,
        originalUnit ? originalUnit : fromUnit, //fromUnit,
        absolutePreviousPeriod,
        MaxValueUnitConversion.Unit
      ).Value;
      actual = fromCarbonKPI
        ? convertedActual
        : getConvertedValue(
            conversionType,
            originalUnit ? originalUnit : fromUnit, //fromUnit,
            actual,
            MaxValueUnitConversion.Unit
          ).Value;
      actualUnit = getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [convertedActual],
        originalUnit
      ).Unit;
      absoluteActual = getConvertedValue(
        conversionType,
        originalUnit ? originalUnit : fromUnit, //fromUnit,
        absoluteActual,
        MaxValueUnitConversion.Unit
      ).Value;
      changeUnit = getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [deviation],
        originalUnit
      ).Unit;
      deviation =
        showChangeWhenData &&
        (isNaN(Number(previousPeriod)) ||
          isNaN(Number(actual)) ||
          Number(actual) === 0 ||
          Number(previousPeriod) === 0)
          ? null
          : fromCarbonKPI
          ? deviation
          : getConvertedValue(
              conversionType,
              originalUnit ? originalUnit : fromUnit, //fromUnit,
              deviation,
              MaxValueUnitConversion.Unit
            ).Value;
    } else {
      previousPeriod = fromCarbonKPI
        ? convertedPreviousPeriod
        : getConvertedValue(
            conversionType,
            originalUnit ? originalUnit : fromUnit, //fromUnit,
            previousPeriod,
            MaxValueUnitConversion.Unit
          ).Value;
      baselineUnit = getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [convertedPreviousPeriod],
        originalUnit
      ).Unit;
      absolutePreviousPeriod = getConvertedValue(
        conversionType,
        originalUnit ? originalUnit : fromUnit, //fromUnit,
        absolutePreviousPeriod,
        MaxValueUnitConversion.Unit
      ).Value;
      absoluteActual = getConvertedValue(
        conversionType,
        originalUnit ? originalUnit : fromUnit, //fromUnit,
        absoluteActual,
        MaxValueUnitConversion.Unit
      ).Value;
      actual = fromCarbonKPI ? convertedActual : MaxValueUnitConversion.Value;
      actualUnit = getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [convertedActual],
        originalUnit
      ).Unit;
      changeUnit = getMaxConvertedValueFromArray(
        conversionType,
        fromUnit,
        [deviation],
        originalUnit
      ).Unit;
      deviation =
        showChangeWhenData &&
        (isNaN(Number(previousPeriod)) ||
          isNaN(Number(actual)) ||
          Number(actual) === 0 ||
          Number(previousPeriod) === 0)
          ? null
          : fromCarbonKPI
          ? deviation
          : getConvertedValue(
              conversionType,
              originalUnit ? originalUnit : fromUnit, //fromUnit,
              deviation,
              MaxValueUnitConversion.Unit
            ).Value;
    }
    ConvertedUnit = MaxValueUnitConversion.Unit;
  }
  let deviationScaledData;
  if (deviation === 0 && originalDeviation !== 0) {
    deviationScaledData = getConvertedValue(
      conversionType,
      originalUnit ? originalUnit : fromUnit, //fromUnit,
      originalDeviation,
      undefined
    );
  }

  return {
    previousPeriod,
    actual,
    absolutePreviousPeriod,
    absoluteActual,
    deviation,
    ConvertedUnit,
    deviationScaledData,
    actualUnit,
    baselineUnit,
    changeUnit
  };
};

export const getConsupSumFromSite = (
  BreakdownBySites: IAny[],
  fieldName: consumFields
) => {
  return BreakdownBySites.reduce((sum, site) => {
    return site && site[fieldName] ? sum + site[fieldName] : sum + 0;
  }, 0);
};

export const getFieldValue = (
  visibleSites: string[],
  item: ISiteEnergyUsageData,
  fieldName: energyFields
) => {
  return visibleSites.includes(item && item.id)
    ? item[fieldName]
    : ZERO_DEFAULT_VALUE;
};

export const isPremiumSubscription = (
  emaasEnergyDataList: IEmaasEnergyUsageData[]
) => {
  const premiumSite = emaasEnergyDataList.find((site) => {
    return (
      site?.subscriptionType?.toLowerCase() ===
      EnergyUsageField.PREMIUMSUBS.toLowerCase()
    );
  });
  return premiumSite ? true : false;
};

export const getEnergyFieldsForDeviation = (
  isPremiumSub: boolean
): { actualField: energyFields; baselineField: energyFields } => {
  if (isPremiumSub) {
    return {
      actualField: 'absoluteTotalConsumption',
      baselineField: 'absoluteTotalBaselineConsumption'
    };
  } else {
    return {
      actualField: 'totalConsumption',
      baselineField: 'totalBaselineConsumption'
    };
  }
};
